import Backbone from 'lib/backbone';
import invokeCallbacks from 'app/NVTagCallbacks';
import FieldView from 'app/views/FieldView';
import FormError from 'app/FormError';
import deviceInfo from 'app/deviceInfo';
// formDefUtils was imported when this was an AMD module. Unsure if it's needed for a side effect
import 'app/FormDefUtils';

var $ = Backbone.$,
  _ = Backbone._;

// Used when a locale supplies no MOMENT_FORMAT, and when one can't be parsed.
var DEFAULT_DATE_FORMAT = 'MM/DD/YYYY';

// Compiled formats, keyed by format string. Formats come from a small fixed set
// of locale resources, so this stays tiny.
var _dateFormatCache = Object.create(null);

// Compiles a format made of Y/M/D runs and literal separators ('MM/DD/YYYY',
// 'DD/MM/YYYY', 'YYYY-MM-DD') into the tokens and pattern used by _formatDate
// and _parseDate. Field order comes from the format rather than being assumed,
// since locales disagree about it -- see MOMENT_FORMAT in
// nvtag/resources/datepicker/Datepicker.*.json. Returns null if the format
// doesn't name all three of year, month and day.
function _compileDateFormat(fmt) {
  var tokens = [], pattern = '', seen = {}, i = 0, char, len, literal;
  while (i < fmt.length) {
    char = fmt.charAt(i);
    len = 1;
    while (fmt.charAt(i + len) === char) {
      len++;
    }
    if (char === 'Y' || char === 'M' || char === 'D') {
      seen[char] = true;
      tokens.push({ part: char, len: len });
      pattern += '(\\d{' + len + '})';
    } else {
      literal = fmt.substr(i, len);
      tokens.push({ literal: literal });
      pattern += literal.replace(/[\\^$.*+?()[\]{}|]/g, '\\$&');
    }
    i += len;
  }
  if (!seen.Y || !seen.M || !seen.D) {
    return null;
  }
  return { tokens: tokens, pattern: new RegExp('^' + pattern + '$') };
}

function _dateFormat(fmt) {
  var key = fmt || DEFAULT_DATE_FORMAT;
  if (!_dateFormatCache[key]) {
    _dateFormatCache[key] = _compileDateFormat(key) || _compileDateFormat(DEFAULT_DATE_FORMAT);
  }
  return _dateFormatCache[key];
}

function _padNumber(value, len) {
  var str = String(value);
  while (str.length < len) {
    str = '0' + str;
  }
  return str.length > len ? str.slice(-len) : str;
}

function _formatDate(date, fmt) {
  var tokens = _dateFormat(fmt).tokens, out = '', i, token, value;
  for (i = 0; i < tokens.length; i++) {
    token = tokens[i];
    if (!token.part) {
      out += token.literal;
      continue;
    }
    if (token.part === 'Y') {
      value = date.getUTCFullYear();
    } else if (token.part === 'M') {
      value = date.getUTCMonth() + 1;
    } else {
      value = date.getUTCDate();
    }
    out += _padNumber(value, token.len);
  }
  return out;
}

function _parseDate(str, fmt) {
  var format = _dateFormat(fmt);
  var match = format.pattern.exec(str);
  if (!match) {
    return null;
  }
  var tokens = format.tokens, group = 0, y, m, d, i, value;
  for (i = 0; i < tokens.length; i++) {
    if (!tokens[i].part) {
      continue;
    }
    value = +match[++group];
    if (tokens[i].part === 'Y') {
      y = value;
    } else if (tokens[i].part === 'M') {
      m = value - 1;
    } else {
      d = value;
    }
  }
  var date = new Date(Date.UTC(y, m, d));
  // Date.UTC rolls out-of-range components over into neighbouring months and
  // years, so compare back against the input to reject dates like 31/02/2026.
  return (date.getUTCFullYear() === y && date.getUTCMonth() === m && date.getUTCDate() === d) ? date : null;
}

function _addMonths(date, n) {
  var d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + n, date.getUTCDate()));
  if (d.getUTCDate() !== date.getUTCDate()) {
    d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + n + 1, 0));
  }
  return d;
}

export default FieldView.extend({
  __name__: 'DateView',
  tagName: 'label',
  className: function () {
    return 'at-date   ' + this.options.definition.name.toLowerCase();
  },
  events: {
    'change': 'renderFeedback'
  },
  initialize: function () {
    FieldView.prototype.initialize.call(this);
    $('.ui-datepicker').remove();

    // this.resources.Datepicker is NOT available on mobile, since we don't use that jquery UI datepicker on mobile
    this.datepickerConfig = this.resources.Datepicker || {};

    var format = this.format = {
      // the output value (returned by this.val())
      value: 'YYYY-MM-DD',
      // The display should be consistent (but not the same) with what the Datepicker uses so the value isn't immediately considered invalid
      // however, MOMENT_FORMAT uses MM, DD, and YYYY as you would expect, while the date picker lib uses mm, dd and yy
      // datepicker docs: https://api.jqueryui.com/datepicker/
      // This format MUST have 10 characters (based on how the value is validated within this.val())
      display: (this.datepickerConfig.MOMENT_FORMAT || DEFAULT_DATE_FORMAT).toUpperCase(), // formatted & parsed by _formatDate/_parseDate, must use M, D and Y chars
      valueFormat: function () {
        return deviceInfo.mobile ? format.value : format.display;
      }
    };

  },
  context: function () {
    var context = {
      name: this.name,
      title: this.title,
      // The hint has to name the fields in the order this.format.display actually parses them, so it
      // lives in the same locale resource as MOMENT_FORMAT. It used to come from a DateFormat string
      // in PrimaryResources, which is translated for only a subset of the locales the Datepicker
      // resources cover (see `supported` in nvtag/resources/PrimaryResources.resx.js) and so told a
      // donor on, say, an en-GB form to type MM/DD/YYYY into a field that reads DD/MM/YYYY. Falling
      // back to the display format leaves the hint in Y/M/D rather than the locale's own letters,
      // which is worse localization but still honest.
      placeholder: (this.datepickerConfig.DATE_FORMAT_PLACEHOLDER || this.format.display).toUpperCase(),
      type: deviceInfo.mobile ? 'date' : 'text',
      labelhide: this.options.labels === 'placeholder',
      required: this.def.required ? 'required' : '',
      value: this.def.default_value ? _formatDate(new Date(this.def.default_value), this.format.valueFormat()) : ''
    };

    if (context.labelhide && context.title) {
      context.placeholder = context.title;
    }
    if (this.name === 'EndDate') {
      context.labelhide = true;
      context.value = context.value || _formatDate(_addMonths(new Date(), 1), this.format.valueFormat());
    }

    return invokeCallbacks('alterContext', { element: this.type, context: context, def: this.def }).context;
  },
  render: function () {
    this.$el.html(this.template(this.context()));
    this.$date = this.$('input');
    if (!deviceInfo.mobile) {
      // set latest possible date for datepicker
      var maxDate;
      if (this.name === 'DateOfBirth') {
        maxDate = 0;
      } else if (this.name === 'NotificationSendDate') {
        maxDate = '+1y';
      }
      // set earliest start date for datepicker
      var minDate;
      if (this.name === 'EndDate' || this.name === 'NotificationSendDate') {
        minDate = 0;
      }
      this.$date.datepicker('destroy');
      // Use localized date picker settings as the base configuration
      var settings = _.extend({}, this.datepickerConfig, {
        maxDate: maxDate,
        minDate: minDate,
        yearRange: '1900:2050',
        changeMonth: true,
        changeYear: true,
        showButtonPanel: false,
        showAnim: ''
      });
      this.$date.datepicker(settings);
    }
    return this;
  },
  errors: function () {
    var errors = [];
    var val = this.val();
    if (val === 'invalid') {
      errors.push(new FormError(this.resources.PrimaryResources.PleaseEnterValidDate, this));
    }
    return FieldView.prototype.errors.call(this, errors);
  },
  val: function () {
    var val = this.$date && this.$date.val();
    if (val) {
      var date = _parseDate(val, this.format.valueFormat());
      return date !== null && val.length === 10 ? _formatDate(date, this.format.value) : 'invalid';
    } else {
      return null;
    }
  },
  setval: function (val) {
    var date = val || this.def.default_value;
    var value = date ? _formatDate(new Date(date), this.format.valueFormat()) : '';
    if (!value && this.name === 'EndDate') {
      value = _formatDate(new Date(), this.format.valueFormat());
    }
    this.$date.val(value);
    return this.touch();
  },
  type: 'date'
});
